/**
 * Authentication Context for Redwood R2R Platform
 * 
 * Provides centralized user authentication and authorization management.
 * Handles user sessions, role-based access control, and project-specific permissions.
 * 
 * Features:
 * - User authentication state management
 * - Role-based access control (RBAC) integration
 * - Project-specific role assignments
 * - Permission checking utilities
 * - Secure session management
 * 
 * Security Notes:
 * - All authentication state is validated server-side
 * - Project roles are checked on each route change
 * - Permissions are enforced at component and API levels
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AccessControl } from '@/utils/accessControl';
import { UserRole, UserPermissions } from '@shared/interfaces/user.interface.ts';
import {User} from "@shared/interfaces/user.interface.ts";
import { useAuth0 } from '@auth0/auth0-react';



// Project-specific role assignment structure
interface ProjectRole {
  project_id: string;   // UUID of the project
  user_id: number;      // User ID from users table
  role: UserRole;       // Specific role within this project
}

// Authentication error types for comprehensive error handling
interface AuthError {
  type: 'network' | 'validation' | 'authorization' | 'session' | 'server';
  message: string;
  code?: string;
  timestamp: Date;
}

// Session status for better state tracking
type SessionStatus = 'idle' | 'authenticating' | 'authenticated' | 'refreshing' | 'expired' | 'error';

// Authentication context interface providing all auth-related functionality
interface AuthContextType {
  user: User | null;                    // Current authenticated user
  currentProjectRole: UserRole | null;  // Role in the currently selected project
  permissions: UserPermissions;
  isLoading: boolean;
  isAuthenticated: boolean;             // Whether user is logged in
  isSuperAdmin: boolean;                // Whether user is SuperAdmin
  impersonatedUser: User | null;        // User being impersonated (if any)
  isImpersonating: boolean;             // Whether currently impersonating
  originalUser: User | null;            // Original SuperAdmin user when impersonating
  sessionStatus: SessionStatus;         // Current session state
  lastError: AuthError | null;          // Last authentication error
  isTokenRefreshing: boolean;           // Whether token refresh is in progress
  sessionExpiry: Date | null;           // When current session expires
  login: (username: string, password: string) => Promise<User | null>;
  logout: () => void;
  setCurrentProject: (projectId: string) => Promise<void>;
  getUserProjectRole: (projectId: string) => Promise<UserRole | null>;
  updateProjectRole: (projectId: string, userId: number, role: UserRole) => Promise<boolean>;
  hasPermission: (permission: keyof UserPermissions) => boolean;
  canAccessProject: (projectId: string) => Promise<boolean>;
  startImpersonation: (targetUserId: number) => Promise<boolean>;
  stopImpersonation: () => Promise<boolean>;
  clearError: () => void;               // Clear current error state
  refreshSession: () => Promise<boolean>; // Manually refresh session
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [currentProjectRole, setCurrentProjectRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('idle');
  const [lastError, setLastError] = useState<AuthError | null>(null);
  const [isTokenRefreshing, setIsTokenRefreshing] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null);
  const { logout: auth0Logout } = useAuth0();

  const permissions = currentProjectRole ? AccessControl.getPermissions(currentProjectRole) : {
    canEdit: false,
    canInvite: false,
    canView: false,
    canExport: false,
    canDelete: false,
    canManageUsers: false,
    canAccessAdmin: false,
    canCreateProjects: false,
    canViewAllProjects: false,
  };

  const isAuthenticated = !!user;
  // Normalize role handling and centralize admin check
  const normalizeRole = (role?: string | null): string | null => {
    if (!role || typeof role !== 'string') return null;
    return role.toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
  };
  const isGlobalAdmin = (role?: string | null): boolean => {
    const r = normalizeRole(role);
    return r === 'superadmin' || r === 'super_admin';
  };
  const isSuperAdmin = isGlobalAdmin(user?.role);
  const isImpersonating = !!impersonatedUser;

  // Helper function to create error objects
  const createError = (type: AuthError['type'], message: string, code?: string): AuthError => ({
    type,
    message,
    code,
    timestamp: new Date()
  });

  // Clear current error state
  const clearError = () => {
    setLastError(null);
  };

  // Update session expiry based on token
  const updateSessionExpiry = (token: string) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp) {
        setSessionExpiry(new Date(payload.exp * 1000));
      }
    } catch (error) {
      console.warn('Failed to parse token expiry:', error);
    }
  };

  // Manual session refresh
  const refreshSession = async (): Promise<boolean> => {
    const token = localStorage.getItem('jwt_token');
    if (!token) {
      setLastError(createError('session', 'No token available for refresh'));
      return false;
    }

    setIsTokenRefreshing(true);
    setSessionStatus('refreshing');

    try {
      const response = await fetch('/api/auth/refresh-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (response.ok) {
        const result = await response.json();
        localStorage.setItem('jwt_token', result.token);
        setUser(result.user);
        updateSessionExpiry(result.token);
        setSessionStatus('authenticated');
        clearError();
        return true;
      } else {
        setLastError(createError('session', 'Token refresh failed'));
        setSessionStatus('expired');
        return false;
      }
    } catch (error) {
      setLastError(createError('network', 'Network error during token refresh'));
      setSessionStatus('error');
      return false;
    } finally {
      setIsTokenRefreshing(false);
    }
  };

  // Check if user has specific permission
  const hasPermission = (permission: keyof UserPermissions): boolean => {
    return permissions[permission];
  };

  const login = async (username: string, password: string): Promise<User | null> => {
    setSessionStatus('authenticating');
    clearError();
    
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Store JWT token in localStorage
        if (result.token) {
          localStorage.setItem('jwt_token', result.token);
          updateSessionExpiry(result.token);
        }
        
        setUser(result.user);
        setSessionStatus('authenticated');
        
        // If user has global admin role, set super_admin for all projects
        if (isGlobalAdmin(result.user.role)) {
          setCurrentProjectRole('super_admin');
        }
        
        // Check for pending invitation claims after successful login
        await checkAndClaimPendingInvitations();
        
        return result.user;
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
        setLastError(createError('authorization', errorData.message || 'Invalid credentials'));
        setSessionStatus('error');
        return null;
      }
    } catch (error) {
      console.error('Login error:', error);
      setLastError(createError('network', 'Unable to connect to server'));
      setSessionStatus('error');
      return null;
    }
  };

  const checkAndClaimPendingInvitations = async () => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    let inviteToken = urlParams.get('invite_token');
    
    // Fallback to localStorage
    if (!inviteToken) {
      inviteToken = localStorage.getItem('invite_token');
    }

    if (!inviteToken) return;

    try {
      const jwtToken = localStorage.getItem('jwt_token');
      const response = await fetch('/api/invitations/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': jwtToken ? `Bearer ${jwtToken}` : '',
        },
        body: JSON.stringify({ token: inviteToken }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Invitation claimed successfully:', result);
        
        // Clean up the token
        localStorage.removeItem('invite_token');
        
        // Remove token from URL if present
        if (urlParams.get('invite_token')) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('invite_token');
          window.history.replaceState({}, '', newUrl.toString());
        }
        
        // Optionally redirect to the first project
        if (result.projects && result.projects.length > 0) {
          // Allow some time for UI to update before redirecting
          setTimeout(() => {
            window.location.href = `/projects/${result.projects[0]}`;
          }, 1000);
        }
      } else {
        // If claim fails, still clean up the token to avoid repeated attempts
        localStorage.removeItem('invite_token');
        console.warn('Failed to claim invitation, token may be expired or invalid');
      }
    } catch (error) {
      console.warn('Error claiming invitation:', error);
      // Clean up token on error to prevent repeated attempts
      localStorage.removeItem('invite_token');
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) return false;

      const response = await fetch('/api/auth/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const result = await response.json();
        localStorage.setItem('jwt_token', result.token);
        setUser(result.user);
        
        // Update role for admin users
        if (isGlobalAdmin(result.user.role)) {
          setCurrentProjectRole('super_admin');
        }
        
        return true;
      } else {
        // Refresh failed, clear token and log out
        localStorage.removeItem('jwt_token');
        setUser(null);
        setCurrentProjectRole(null);
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      localStorage.removeItem('jwt_token');
      setUser(null);
      setCurrentProjectRole(null);
      return false;
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('jwt_token');
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear session state and token locally
    setUser(null);
    setCurrentProjectRole(null);
    localStorage.removeItem('jwt_token');

    // End Auth0 session and return to app root
    try {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      return; // SDK navigates away
    } catch (e) {
      // Fallback redirect
      if (typeof window !== 'undefined') {
        window.location.assign('/');
      }
    }
  };

  const getUserProjectRole = async (projectId: string): Promise<UserRole | null> => {
    if (!user) return null;

    // Super admins have access to all projects
    if (isGlobalAdmin(user.role)) {
      return 'super_admin';
    }

    try {
      const response = await fetch(`/api/project-roles/${projectId}/${user.id}`);
      if (response.ok) {
        const roleData = await response.json();
        return roleData.role as UserRole;
      }
      return null;
    } catch (error) {
      console.error('Error fetching project role:', error);
      return null;
    }
  };

  const setCurrentProject = async (projectId: string): Promise<void> => {
    if (!user) return;

    const role = await getUserProjectRole(projectId);
    setCurrentProjectRole(role);
  };

  const updateProjectRole = async (
    projectId: string, 
    userId: number, 
    role: UserRole
  ): Promise<boolean> => {
    if (!currentProjectRole || !AccessControl.canManageUsers(currentProjectRole)) {
      await AccessControl.logUnauthorizedAccess(
        projectId,
        user?.id.toString() || 'unknown',
        'update_project_role',
        currentProjectRole || 'viewer'
      );
      return false;
    }

    try {
      const response = await fetch('/api/project-roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: projectId,
          user_id: userId,
          role,
        }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating project role:', error);
      return false;
    }
  };

  const canAccessProject = async (projectId: string): Promise<boolean> => {
    if (!user) return false;

    // Super admins can access all projects
    if (isGlobalAdmin(user.role)) {
      return true;
    }

    try {
      const response = await fetch(`/api/projects/${projectId}/access-check`);
      return response.ok;
    } catch (error) {
      console.error('Error checking project access:', error);
      return false;
    }
  };

  // Initialize user from stored session
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip authentication for public routes and token-gated flows (align with AuthRouteGuard)
      const publicChecks = [
        (p: string) => p === '/login',
        (p: string) => p.startsWith('/share/'),
        (p: string) => p.startsWith('/public/'),
        (p: string) => p.startsWith('/value-assessment/public-input'),
        (p: string) => p.startsWith('/value-assessment/input'),
        (p: string) => /\/value-assessment\/[^/]+\/public-intro/.test(p),
      ];
      const path = window.location.pathname;
      const qsHasToken = (() => { try { return new URLSearchParams(window.location.search).has('t'); } catch { return false; } })();
      const cachedToken = (() => { try { return Boolean(sessionStorage.getItem('public_token')); } catch { return false; } })();
      const isPublicRoute = publicChecks.some(fn => fn(path)) || qsHasToken || cachedToken;

      if (isPublicRoute) {
        setIsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('jwt_token');
        if (!token) {
          // No token on a protected route: ensure clean state and redirect to login
          try {
            localStorage.removeItem('jwt_token');
          } catch {}
          setUser(null);
          setCurrentProjectRole(null);
          setIsLoading(false);
          try { window.location.assign('/login'); } catch {}
          return;
        }

        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData.user);
          
          // Set super admin role for global admins
          if (isGlobalAdmin(userData.user.role)) {
            setCurrentProjectRole('super_admin');
          }
        } else if (response.status === 401) {
          // Token is invalid or expired: clear and redirect to login
          try { localStorage.removeItem('jwt_token'); } catch {}
          setUser(null);
          setCurrentProjectRole(null);
          try { window.location.assign('/login'); } catch {}
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Set a default state for unauthenticated users to prevent flow breakage
        setUser(null);
        setCurrentProjectRole(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up automatic token refresh (every 45 minutes)
  useEffect(() => {
    if (!user) return;

    const refreshInterval = setInterval(async () => {
      const token = localStorage.getItem('jwt_token');
      if (token) {
        console.log('Proactively refreshing JWT token...');
        await refreshToken();
      }
    }, 45 * 60 * 1000); // 45 minutes

    return () => clearInterval(refreshInterval);
  }, [user]);

  const startImpersonation = async (targetUserId: number): Promise<boolean> => {
    try {
      const token = localStorage.getItem('jwt_token');
      const response = await fetch('/api/admin/impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ targetUserId }),
      });

      if (response.ok) {
        const data = await response.json();
        setOriginalUser(user); // Store current user as original
        setImpersonatedUser(data.impersonatedUser);
        setUser(data.impersonatedUser); // Switch to impersonated user
        
        // Update role for impersonated user
        if (isGlobalAdmin(data.impersonatedUser.role)) {
          setCurrentProjectRole('super_admin');
        } else {
          setCurrentProjectRole('collaborator'); // Default role for regular users
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      return false;
    }
  };

  const stopImpersonation = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('jwt_token');
      const response = await fetch('/api/admin/stop-impersonation', {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Restore original user
        setUser(originalUser);
        setImpersonatedUser(null);
        setOriginalUser(null);
        
        // Restore original user's role
        if (isGlobalAdmin(originalUser?.role)) {
          setCurrentProjectRole('super_admin');
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    currentProjectRole,
    permissions,
    isLoading,
    isAuthenticated: !!user,
    isSuperAdmin: isGlobalAdmin(user?.role),
    impersonatedUser,
    isImpersonating: !!impersonatedUser,
    originalUser,
    sessionStatus,
    lastError,
    isTokenRefreshing,
    sessionExpiry,
    login,
    logout,
    setCurrentProject,
    getUserProjectRole,
    updateProjectRole,
    hasPermission,
    canAccessProject,
    startImpersonation,
    stopImpersonation,
    clearError,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook for checking if user has specific role
export function useRole() {
  const { currentProjectRole } = useAuth();
  return {
    role: currentProjectRole,
    isSuperAdmin: currentProjectRole === 'super_admin',
    isOwner: currentProjectRole === 'owner',
    isCollaborator: currentProjectRole === 'collaborator',
    isViewer: currentProjectRole === 'viewer',
    hasElevatedAccess: currentProjectRole ? AccessControl.hasElevatedAccess(currentProjectRole) : false,
    isReadOnly: currentProjectRole ? AccessControl.isReadOnly(currentProjectRole) : true,
  };
}

// Component for displaying user role badge
export function RoleBadge({ role, className = "" }: { role: UserRole; className?: string }) {
  const badgeColor = AccessControl.getRoleBadgeColor(role);
  const displayName = AccessControl.getRoleDisplayName(role);
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeColor} ${className}`}>
      {displayName}
    </span>
  );
}
